import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Modal, Form, message, Avatar, Tag, Row, Col, Select, Checkbox } from 'antd';
import { useDispatch } from 'react-redux';
import { postData } from '../../../actions/developer';
import { getData } from '../../../actions/apiCall';
import axios from 'axios';
import { getAccess } from '../../../actions/auth';
import dayjs from 'dayjs';
import RenderFormItem from '../../../components/Forms/FormItems';
import { FormColumnConfig } from '../../../interfaces/entity';

interface AddFeedbackModalProps {
    visible: boolean;
    onClose: () => void;
    onSuccess: () => void;
    interviewData?: any; // Interview data containing developer info, interview details, etc.
}


const AddFeedbackModal: React.FC<AddFeedbackModalProps> = ({
    visible,
    onClose,
    onSuccess,
    interviewData
}) => {
    const [form] = Form.useForm();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [interviewLevels, setInterviewLevels] = useState<{ value: string; label: string }[]>([]);
    const [loadingLevels, setLoadingLevels] = useState(false);
    const [interviews, setInterviews] = useState<{ value: string; label: string; interview?: any }[]>([]);
    const [loadingInterviews, setLoadingInterviews] = useState(false);
    const [selectedInterview, setSelectedInterview] = useState<any>(null);
    const [isVendorDeveloper, setIsVendorDeveloper] = useState(false);
    const [loadingDeveloper, setLoadingDeveloper] = useState(false);

    // Fetch interview levels from API
    useEffect(() => {
        const fetchInterviewLevels = async () => {
            setLoadingLevels(true);
            try {
                const response = await dispatch(getData('/api/calendar/interviews/levels'));
                if (response?.success && response?.data) {
                    setInterviewLevels(response.data);
                } else if (response?.code === 200 && response?.data) {
                    setInterviewLevels(response.data);
                }
            } catch (error) {
                console.error('Error fetching interview levels:', error);
                // Fallback to default levels if API fails
                setInterviewLevels([
                    { value: 'Screening', label: 'Screening' },
                    { value: 'Coding', label: 'Coding' },
                    { value: 'Technical', label: 'Technical' },
                    { value: 'Level 1', label: 'Level 1' },
                    { value: 'Level 2', label: 'Level 2' },
                    { value: 'Level 3', label: 'Level 3' },
                    { value: 'HR', label: 'HR' },
                    { value: 'Manager', label: 'Manager' },
                    { value: 'Final', label: 'Final' }
                ]);
            } finally {
                setLoadingLevels(false);
            }
        };
        fetchInterviewLevels();
    }, []);

    // Fetch interviews for the developer
    useEffect(() => {
        const fetchInterviews = async () => {
            if (!interviewData?._id && !interviewData?.developer?._id) {
                setInterviews([]);
                return;
            }

            setLoadingInterviews(true);
            try {
                const developerId = interviewData?._id || interviewData?.developer?._id || interviewData?.developer;
                if (!developerId) {
                    setInterviews([]);
                    return;
                }

                axios.defaults.headers.common['permissions'] = JSON.stringify(getAccess());
                const response = await dispatch(
                    getData(`/calendar/interviews/developer/${developerId}`)
                ) as any;

                if (response?.code === 200 && response?.data?.interviews) {
                    const interviewOptions = response.data.interviews.map((interview: any) => {
                        const interviewDate = interview.date ? dayjs(interview.date).format('DD MMM, YYYY') : 'N/A';
                        const interviewTime = interview.time || 'N/A';
                        const interviewType = interview.interview_type || interview.interviewTitle || 'Interview';
                        const interviewerName = interview.interviewerName ? ` (${interview.interviewerName})` : '';
                        const accountName = interview.accountName ? ` [${interview.accountName}]` : '';
                        return {
                            value: interview._id || interview.interviewId,
                            label: `${interviewType}${accountName} - ${interviewDate} ${interviewTime}${interviewerName}`,
                            interview: {
                                ...interview,
                                developer: interview.developer || interviewData?._id || interviewData?.developer?._id || interviewData?.developer
                            }
                        };
                    });
                    setInterviews(interviewOptions);
                } else {
                    setInterviews([]);
                }
            } catch (error) {
                console.error('Error fetching interviews:', error);
                setInterviews([]);
            } finally {
                setLoadingInterviews(false);
            }
        };

        if (visible) {
            fetchInterviews();
        }
    }, [visible, interviewData, dispatch]);

    // Fetch developer details to check if from vendor
    useEffect(() => {
        const fetchDeveloperDetails = async () => {
            if (!visible) {
                setIsVendorDeveloper(false);
                return;
            }

            const developerId = interviewData?._id || interviewData?.developer?._id || interviewData?.developer;
            if (!developerId) {
                setIsVendorDeveloper(false);
                return;
            }

            setLoadingDeveloper(true);
            try {
                axios.defaults.headers.common['permissions'] = JSON.stringify(getAccess());
                const response = await dispatch(getData(`/bench-developers/${developerId}`)) as any;

                if (response?.code === 200 || response?.success) {
                    const developerData = response?.data || response?.data?.data;
                    // Check if developer has vendorPartner
                    setIsVendorDeveloper(!!(developerData?.vendorPartner || (developerData?.vendorPartner && typeof developerData.vendorPartner === 'object')));
                } else {
                    setIsVendorDeveloper(false);
                }
            } catch (error) {
                console.error('Error fetching developer details:', error);
                setIsVendorDeveloper(false);
            } finally {
                setLoadingDeveloper(false);
            }
        };

        fetchDeveloperDetails();
    }, [visible, interviewData, dispatch]);

    // Handle interview selection change
    const handleInterviewChange = useCallback((value: string) => {
        const selected = interviews.find(int => int.value === value);
        if (selected?.interview) {
            setSelectedInterview(selected.interview);
        }
    }, [interviews]);

    // Form fields configuration using FormColumnConfig
    const formFields: FormColumnConfig[] = useMemo(() => [
        {
            name: 'interview',
            label: 'Select Interview',
            type: 'select',
            required: true,
            placeholder: 'Select Interview',
            span: 24,
            rules: [{ required: true, message: 'Please select an interview' }],
            children: interviews.map(int => ({ name: int.label, value: int.value })),
            selectProps: {
                loading: loadingInterviews,
                showSearch: true,
                filterOption: (input: string, option: any) =>
                    (option?.children || '').toLowerCase().includes(input.toLowerCase()),
                onChange: handleInterviewChange
            }
        },
        {
            name: 'status',
            label: 'Status',
            type: 'select',
            required: true,
            placeholder: 'Select Status',
            span: 24,
            rules: [{ required: true, message: 'Please select status' }],
            children: [
                { name: 'Selected', value: 'CLEARED' },
                { name: 'Rejected', value: 'REJECTED' },
                { name: 'On Hold', value: 'HOLD' },
                { name: 'Postpone', value: 'PENDING' }
            ]
        },
        {
            name: 'comments',
            label: 'Comment',
            type: 'textarea',
            required: true,
            placeholder: 'Comment',
            span: 24,
            rules: [{ required: true, message: 'Please enter comment' }],
            inputTextAreaProps: {
                rows: 4
            }
        },
        ...(isVendorDeveloper ? [{
            name: 'notifyVendor',
            label: 'Send notification of feedback to vendor',
            type: 'checkbox' as any,
            span: 24,
            checkboxProps: {
                defaultChecked: false
            }
        }] : [])
    ], [interviewLevels, interviews, loadingInterviews, handleInterviewChange, isVendorDeveloper]);

    useEffect(() => {
        if (visible) {
            form.resetFields();
            setSelectedInterview(null);
            form.setFieldsValue({ notifyVendor: false }); // Reset checkbox
            // Pre-populate form if interviewData is provided and has interviewId
            if (interviewData?.interviewId || interviewData?._id) {
                const interviewId = interviewData.interviewId || interviewData._id;
                // Try to find and select the interview from the list
                const foundInterview = interviews.find(int => int.value === interviewId);
                if (foundInterview) {
                    form.setFieldsValue({ interview: interviewId });
                    setSelectedInterview(foundInterview.interview);
                }

                // Use interview level directly (already in correct enum format)
                const getInterviewLevelForForm = (level: string | undefined): string | null => {
                    if (!level) return null;
                    // Return the level as-is since backend now uses proper enum values
                    // Check if it exists in the fetched levels
                    const levelExists = interviewLevels.some(l => l.value === level);
                    return levelExists ? level : null;
                };

                const mapStatusToForm = (status: string | undefined): string | null => {
                    if (!status) return null;
                    const statusUpper = status.toUpperCase();
                    if (statusUpper === 'CLEARED' || statusUpper === 'SELECTED') return 'CLEARED';
                    if (statusUpper === 'REJECTED') return 'REJECTED';
                    if (statusUpper === 'HOLD' || statusUpper.includes('HOLD')) return 'HOLD';
                    if (statusUpper === 'PENDING') return 'PENDING';
                    return null;
                };

                form.setFieldsValue({
                    interviewLevel: getInterviewLevelForForm(interviewData.interviewLevel || interviewData.interview?.interviewLevel || interviewData.interview_type),
                    status: mapStatusToForm(interviewData.status || interviewData.interviewStatus)
                });
            }
        }
    }, [visible, interviewData, form, interviews]);

    const handleSubmit = async (values: any) => {
        // Get interview ID from form selection or from interviewData
        const interviewId = values.interview || interviewData?._id || interviewData?.interviewId;

        if (!interviewId) {
            message.error('Please select an interview');
            return;
        }

        setLoading(true);
        try {
            // Get developer ID from selected interview or interviewData
            let developerId = null;
            if (selectedInterview?.developer) {
                developerId = selectedInterview.developer._id || selectedInterview.developer;
            } else {
                developerId = interviewData?.developer?._id || interviewData?.developer || interviewData?._id;
            }

            if (!developerId) {
                message.error('Developer information is missing');
                setLoading(false);
                return;
            }

            // Get interview level from selected interview
            const interviewLevel = selectedInterview?.interview_type || selectedInterview?.interviewTitle;

            if (!interviewLevel) {
                message.error('Interview level is missing. Please select an interview.');
                setLoading(false);
                return;
            }

            // Map values to backend enum format
            // interviewLevel should be lowercase "interview" (e.g., "L1 interview", "Screening interview")
            // status should be uppercase (e.g., "REJECTED", "CLEARED", "HOLD", "PENDING")
            const payload = {
                developer: developerId,
                interviewLevel: interviewLevel, // From selected interview
                status: values.status, // Already in correct format from form
                comments: values.comments,
                notifyVendor: values.notifyVendor || false // Send notification to vendor if checked
            };

            axios.defaults.headers.common['permissions'] = JSON.stringify(getAccess());

            const response = await dispatch(postData(`/calendar/interviews/${interviewId}/feedback`, payload));

            if (response?.code === 200 || response?.code === 201 || response?.success) {
                message.success('Feedback submitted successfully!');
                form.resetFields();
                setSelectedInterview(null);
                onSuccess();
                onClose();
            } else {
                message.error(response?.message || 'Failed to submit feedback');
            }
        } catch (error: any) {
            console.error('Error submitting feedback:', error);
            const errorMessage = error?.response?.data?.message || error?.message || 'Failed to submit feedback';

            // Handle duplicate feedback error (409 Conflict)
            if (error?.response?.status === 409 || error?.code === 409 || errorMessage.includes('already submitted feedback')) {
                message.warning({
                    content: errorMessage || 'You have already submitted feedback for this interview. Please use the reply feature to add additional comments.',
                    duration: 5,
                    style: { marginTop: '20vh' }
                });
            } else {
                message.error({
                    content: errorMessage,
                    duration: 5,
                    style: { marginTop: '20vh' }
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        form.resetFields();
        onClose();
    };

    // Get developer info from interviewData
    const developer = interviewData?.developer || interviewData?.benchDev || {};
    const developerName = developer.firstName && developer.lastName
        ? `${developer.firstName} ${developer.lastName}`
        : developer.name || 'N/A';
    const developerEmail = developer.email || interviewData?.email || 'N/A';
    const developerRole = developer.role || developer.jobTitle || interviewData?.role || 'N/A';
    const currentLevel = interviewData?.interview_type || interviewData?.interviewTitle || 'N/A';

    return (
        <Modal
            title="Submit Feedback"
            open={visible}
            onCancel={handleCancel}
            onOk={() => form.submit()}
            confirmLoading={loading}
            width={600}
            maskClosable={false}
            okText="Submit"
            cancelText="Cancel"
        >
            {/* Interview Detail Section */}
            {interviewData && (
                <div className="mb-6 pb-4 border-b border-gray-200">
                    <div className="flex items-center gap-4 mb-4">
                        <Avatar size={48} className="bg-blue-100 text-blue-600 font-bold">
                            {developerName.charAt(0).toUpperCase()}
                        </Avatar>
                        <div className="flex-1">
                            <div className="font-semibold text-base text-gray-800">{developerName}</div>
                            <div className="text-sm text-gray-500">{developerEmail}</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm text-gray-600">Role:</span>
                        <span className="text-sm font-medium text-gray-800">{developerRole}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600">Current Level:</span>
                        <Tag color="blue" className="m-0">{currentLevel}</Tag>
                    </div>
                </div>
            )}

            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
            >
                <Row gutter={16}>
                    {formFields.map((field) => (
                        <Col span={field.span || 24} key={field.name}>
                            <RenderFormItem column={field} form={form} />
                        </Col>
                    ))}
                </Row>
            </Form>
        </Modal>
    );
};

export default AddFeedbackModal;
